import {ArrowForward} from '@material-ui/icons'
import CloseIcon from '@mui/icons-material/Close'
import {Check as CheckIcon, Repeat as RepeatIcon} from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton
} from '@mui/material'
import React, {useState, useMemo, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {usePersistedRmcOrder} from '../../../../common/react-query/hooks/queries/usePersistedRmcOrder'
import {useJobSitesForModal} from '../hooks/useJobSitesForModal'
import {useRmcOrderIntakeOptionsV2} from '../hooks/useRmcOrderIntakeOptionsV2'
import {OrderIntakeSite} from '../types/OrderIntakeSite.types'
import {
  hasPersistedOrders,
  validatePersistedOrderMaterials
} from '../utils/persistedOrderValidation'

import {useJobSiteSelectionModalStyles} from './JobSiteSelectionModal.styles'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useRolesByCustomerId} from '../../../../Roles'
import {selectSelectedCustomerFilter} from '../../../../Organisms/Customers/Customers.selector'
import {useSelector} from 'react-redux'
import {AppState} from '../../../../Root.store'
import {JobSiteCreationProcess} from '../../../shipToCreation/JobSiteCreationProcess'
import {useFeaturesChecker} from '@hconnect/common/components/FeaturesCheck'
import {api} from '../../../../App.global'

interface JobSiteSelectionModalProps {
  open: boolean
  customerId: string
  onClose: () => void
  onSelectSite: (site: OrderIntakeSite) => void
  onCreateNewOrder: (site: OrderIntakeSite) => void
  onRepeatPreviousOrder: (site: OrderIntakeSite) => void
}

interface JobSitesListProps {
  isLoading: boolean
  sites: OrderIntakeSite[]
  selectedSite: OrderIntakeSite | null
  isFetchingNextPage: boolean
  hasNextPage: boolean | undefined
  onSiteSelect: (site: OrderIntakeSite) => void
  styles: ReturnType<typeof useJobSiteSelectionModalStyles>
  t: any
}

export const JobSiteSelectionModal: React.FC<JobSiteSelectionModalProps> = ({
  open,
  customerId,
  onClose,
  onSelectSite,
  onCreateNewOrder,
  onRepeatPreviousOrder
}) => {
  const {t} = useTranslation()
  const styles = useJobSiteSelectionModalStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSite, setSelectedSite] = useState<OrderIntakeSite | null>(null)
  const [materialValidationError, setMaterialValidationError] = useState<string>('')
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false)
  const {calcEnabled: isFeatureToggleEnabled} = useFeaturesChecker(api)
  const isShipToCreationEnabled = isFeatureToggleEnabled('enableOrderIntakeShipToCreation')

  const selectedCustomer = useSelector((state: AppState) => selectSelectedCustomerFilter(state))
  const {data: roles} = useRolesByCustomerId(selectedCustomer?.customerId)

  const createNewJobSiteProps = useMemo(() => {
    if (!selectedCustomer || !roles) {
      return null
    }
    const orgUnitId = roles.find((role) =>
      role.dataScope.customerIds?.includes(selectedCustomer?.customerId ?? '')
    )?.dataScope.orgUnitId
    return {
      customerId: selectedCustomer?.customerId ?? '',
      customerName: selectedCustomer?.customerName ?? '',
      country: selectedCustomer?.countryId ?? '',
      orgUnitId: orgUnitId ?? ''
    }
  }, [roles, selectedCustomer?.customerId])

  // Query for persisted orders for the selected site
  const {data: persistedOrders, isLoading: isLoadingPersistedOrders} = usePersistedRmcOrder(
    selectedSite?.siteNumber || ''
  )

  const isLoadingPersistedOrdersForSelectedSite =
    isLoadingPersistedOrders && selectedSite?.siteNumber

  // Query RMC options only when needed for validation (when repeat button is clicked)
  const {refetch: refetchRmcOptions, isFetching: isValidatingMaterials} =
    useRmcOrderIntakeOptionsV2(
      selectedSite?.customerId,
      selectedSite?.siteId,
      selectedSite?.salesOffice,
      selectedSite?.marketArea,
      false // Don't auto-fetch, only when we need to validate
    )

  // Check if repeat order button should be enabled (just check if persisted orders exist)
  const canShowRepeatOrderButton = useMemo(() => {
    if (!selectedSite) return false
    return hasPersistedOrders(persistedOrders)
  }, [selectedSite, persistedOrders])

  // Reset validation error when site changes
  useEffect(() => {
    setMaterialValidationError('')
  }, [selectedSite])

  // Reset search and selection when modal closes^
  useEffect(() => {
    if (!open) {
      setSearchTerm('')
      setSelectedSite(null)
    }
  }, [open])

  const {data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage} = useJobSitesForModal(
    open,
    customerId,
    searchTerm
  )

  // Handle infinite scrolling
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const {scrollTop, scrollHeight, clientHeight} = event.currentTarget
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5

    if (isNearBottom && hasNextPage && !isFetchingNextPage && !isLoading) {
      void fetchNextPage()
    }
  }

  // Flatten the paginated data
  const sites = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flat()
  }, [data])

  const handleSiteSelect = (site: OrderIntakeSite) => {
    setSelectedSite(site)
    onSelectSite(site)
  }

  const handleCreateNewJobSite = () => {
    trackEvent('rmcCreateNewJobSite', {
      businessLine: 'RMC',
      customerId: selectedCustomer?.customerId,
      customerName: selectedCustomer?.customerName,
      country: selectedCustomer?.countryId
    })

    setIsCreationModalOpen(true)
  }

  const handleCreateNewOrder = () => {
    if (selectedSite) {
      onCreateNewOrder(selectedSite)
    }
  }

  const handleRepeatPreviousOrder = async () => {
    if (!selectedSite || !persistedOrders) {
      return
    }

    // When button is clicked, fetch RMC options and validate materials
    setMaterialValidationError('') // Clear previous errors

    try {
      // Fetch the latest RMC options
      const result = await refetchRmcOptions()
      const latestRmcOptions = result.data

      if (!latestRmcOptions) {
        setMaterialValidationError(
          t('orderIntake.rmc.jobSiteModal.materialValidation.unableToFetchData')
        )
        return
      }

      // Validate if persisted order materials are still available
      const areMaterialsValid = validatePersistedOrderMaterials(persistedOrders, latestRmcOptions)

      if (areMaterialsValid) {
        if (onRepeatPreviousOrder) {
          onRepeatPreviousOrder(selectedSite)
        }
      } else {
        setMaterialValidationError(
          t('orderIntake.rmc.jobSiteModal.materialValidation.orderNoLongerAvailable')
        )
      }
    } catch (error) {
      console.error('Error validating materials:', error)
      setMaterialValidationError(
        t('orderIntake.rmc.jobSiteModal.materialValidation.unableToValidate')
      )
    }
  }

  if (isShipToCreationEnabled && isCreationModalOpen && createNewJobSiteProps) {
    return (
      <JobSiteCreationProcess
        {...createNewJobSiteProps}
        onClose={() => setIsCreationModalOpen(false)}
        onCreateNewOrder={onCreateNewOrder}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-test-id="jobsite-selection-modal"
      PaperProps={{
        sx: styles.dialogPaper
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6" component="div" sx={styles.titleText}>
          {t('orderIntake.rmc.jobSiteModal.title')}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        {/* Search Input */}
        <Box sx={styles.searchBox}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('orderIntake.rmc.jobSiteModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-test-id="jobsite-search-input"
          />
        </Box>

        {/* Job Sites List */}
        <Box sx={styles.listContainer} onScroll={handleScroll}>
          <JobSitesList
            isLoading={isLoading}
            sites={sites}
            selectedSite={selectedSite}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onSiteSelect={handleSiteSelect}
            styles={styles}
            t={t}
          />
        </Box>

        {!isLoading && isShipToCreationEnabled && (
          <Box sx={styles.createJobSiteBox}>
            <Typography variant="body2" sx={styles.createJobSiteText}>
              {t('orderIntake.rmc.jobSiteModal.jobSiteNotInTheList')}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCreateNewJobSite}
              data-test-id="create-shipTo-button"
              sx={styles.createJobSiteButton}
            >
              {t('orderIntake.rmc.jobSiteModal.addNewJobSiteButton')}
            </Button>
          </Box>
        )}

        {/* Action Buttons */}
        <ActionButtons
          selectedSite={selectedSite}
          canShowRepeatOrderButton={canShowRepeatOrderButton}
          isValidatingMaterials={isValidatingMaterials}
          isLoadingPersistedOrdersForSelectedSite={isLoadingPersistedOrdersForSelectedSite}
          materialValidationError={materialValidationError}
          onCreateNewOrder={handleCreateNewOrder}
          onRepeatPreviousOrder={handleRepeatPreviousOrder}
          styles={styles}
          t={t}
        />
      </DialogContent>
    </Dialog>
  )
}

const JobSitesList: React.FC<JobSitesListProps> = ({
  isLoading,
  sites,
  selectedSite,
  isFetchingNextPage,
  hasNextPage,
  onSiteSelect,
  styles,
  t
}) => {
  if (isLoading && sites.length === 0) {
    return (
      <Box sx={styles.loadingBox}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={styles.loadingText}>
          {t('orderIntake.rmc.jobSiteModal.loadingJobSites')}
        </Typography>
      </Box>
    )
  }

  if (sites.length === 0) {
    return (
      <Box sx={styles.emptyStateBox}>
        <Typography sx={styles.emptyStateText}>
          {t('orderIntake.rmc.jobSiteModal.noJobSitesFound')}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <List sx={styles.list} data-test-id="jobsite-list">
        {sites.map((site, index) => {
          const isSelected = selectedSite?.siteId === site.siteId

          return (
            <ListItem key={site.siteId} sx={styles.listItem}>
              <ListItemButton
                onClick={() => onSiteSelect(site)}
                selected={isSelected}
                data-test-id={`jobsite-option-${site.siteId}`}
                sx={styles.listItemButton(isSelected, index === sites.length - 1)}
              >
                <ListItemText
                  primary={<Typography sx={styles.listItemPrimary}>{site.siteName},</Typography>}
                  secondary={
                    <>
                      <Typography sx={styles.listItemSecondary}>{site.city}</Typography>
                    </>
                  }
                  sx={styles.listItemText}
                />
                {isSelected && <CheckIcon sx={styles.checkIcon} />}
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>

      {isFetchingNextPage && (
        <Box sx={styles.loadingMoreBox}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={styles.loadingMoreText}>
            {t('orderIntake.rmc.jobSiteModal.loadingMore')}
          </Typography>
        </Box>
      )}

      {!hasNextPage && sites.length > 0 && (
        <Typography variant="body2" sx={styles.endOfListText}>
          {t('orderIntake.rmc.jobSiteModal.noMoreJobSites')}
        </Typography>
      )}
    </>
  )
}

interface ActionButtonsProps {
  selectedSite: OrderIntakeSite | null
  canShowRepeatOrderButton: boolean
  isValidatingMaterials: boolean
  isLoadingPersistedOrdersForSelectedSite: string | false | undefined
  materialValidationError: string
  onCreateNewOrder: () => void
  onRepeatPreviousOrder: () => void
  styles: ReturnType<typeof useJobSiteSelectionModalStyles>
  t: any
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  selectedSite,
  canShowRepeatOrderButton,
  isValidatingMaterials,
  isLoadingPersistedOrdersForSelectedSite,
  materialValidationError,
  onCreateNewOrder,
  onRepeatPreviousOrder,
  styles,
  t
}) => {
  return (
    <Box sx={styles.actionButtonsBox}>
      <Button
        variant="contained"
        startIcon={<ArrowForward style={{fontSize: '20px'}} />}
        fullWidth
        disabled={!selectedSite}
        onClick={onCreateNewOrder}
        data-test-id="create-order-button"
        sx={styles.createOrderButton}
      >
        {t('orderIntake.rmc.jobSiteModal.createNewOrder')}
      </Button>

      <Button
        variant="outlined"
        fullWidth
        disabled={!canShowRepeatOrderButton || isValidatingMaterials}
        onClick={onRepeatPreviousOrder}
        data-test-id="repeat-order-button"
        sx={styles.repeatOrderButton}
      >
        <Box sx={styles.repeatOrderButtonContent}>
          {isValidatingMaterials || isLoadingPersistedOrdersForSelectedSite ? (
            <CircularProgress size={16} />
          ) : (
            <RepeatIcon sx={styles.repeatOrderIcon} />
          )}
          {isValidatingMaterials
            ? t('orderIntake.rmc.jobSiteModal.materialValidation.validatingOrder')
            : t('orderIntake.rmc.jobSiteModal.repeatPreviousOrder', 'Repeat the previous order')}
        </Box>
      </Button>

      {materialValidationError ? (
        <Typography variant="body2" sx={styles.errorText}>
          {materialValidationError}
        </Typography>
      ) : (
        <Typography variant="body2" sx={styles.repeatOrderNote}>
          {t(
            'orderIntake.rmc.jobSiteModal.repeatOrderNote',
            'Quickly reorder all materials from your most recent order with a single click.'
          )}
        </Typography>
      )}
    </Box>
  )
}
